'use client';

import { createContext, useContext, useMemo } from 'react';
import { Auth } from '~/modules/auth/client';
import { useI18n } from '~/features/i18n';
import type { SpecificRFC } from '~/utils/react';
import { usePortalCtx } from '../global/portal';
import type { Money } from '@funkey/api/src/modules/units/Money';
import { WEB_VAT_RATE_DEFAULT } from '@funkey/constants';

export { WEB_VAT_RATE_DEFAULT };
import { LocalJSONStorage } from '~/utils/js';
import { useLocalStorageState } from '~/modules/browser/storage';

const ShouldDisplayPricesOverrideCtx = createContext<boolean | null>(null);

/**
 * Force `useShouldDisplayPrices` to return `true` for all descendants.
 * Used on the bizz selection page where admins always need to see prices.
 */
export const ForceDisplayPrices: React.FC<React.PropsWithChildren> = ({ children }) => (
    <ShouldDisplayPricesOverrideCtx.Provider value={true}>
        {children}
    </ShouldDisplayPricesOverrideCtx.Provider>
);

export function useShouldDisplayPrices(): boolean {
    // Every hook runs on every render: the compiler adds its own cache slot here, so a
    // hook after the override's early return changes the hook count between renders.
    const override = useContext(ShouldDisplayPricesOverrideCtx);
    const portal = usePortalCtx();
    const auth = Auth.useSession();

    if (override !== null) return override;

    if (portal.portal !== 'BIZZ') return true;

    // We won't do this again. This is unmaintainable
    if (portal.bizz.slug === 'lidl') return false;

    // Public booking pages always should display prices
    if (!auth.user) {
        // TODO: clean this up and make bizz able to specify permissions for
        // anonymous users
        return true;
    }

    return portal.permissions.includes('BIZZ_VIEW_PRICES');
}

type VATPreference = { enabled: boolean; rate: number };

/**
 * Imperative VAT-preference store. Exposed so non-React code (e.g. AI chat
 * `onFinish` handlers) can flip the preference without going through the
 * `useVATInclPreference` hook — the hook still picks up the change via the
 * storage's dispatched event, so the grid + filters re-render automatically.
 */
export const VAT_PREF_STORAGE = new LocalJSONStorage<VATPreference>(
    'funkey:vat-preference',
    { enabled: false, rate: WEB_VAT_RATE_DEFAULT },
    'localStorage',
);

/** Persistent user preference to show prices VAT-inclusive (WEB portal only). */
export function useVATInclPreference() {
    const storage = useLocalStorageState(VAT_PREF_STORAGE);

    const setEnabled = useMemo(
        () => (value: boolean | React.SetStateAction<boolean>) =>
            storage.setState(prev => ({
                ...prev,
                enabled: typeof value === 'function' ? value(prev.enabled) : value,
            })),
        [storage.setState],
    );

    const setRate = useMemo(
        () => (value: number | React.SetStateAction<number>) =>
            storage.setState(prev => ({
                ...prev,
                rate: typeof value === 'function' ? value(prev.rate) : value,
            })),
        [storage.setState],
    );

    return {
        enabled: storage.state.enabled,
        rate: storage.state.rate,
        setEnabled,
        setRate,
    };
}

export function useVATRate() {
    const portalCtx = usePortalCtx();
    /** After moving to next15, we starting getting errors where portal was `null`. Origin: `RecentlySeenSection.tsx` */
    const portal = portalCtx?.portal ?? 'WEB';
    const vatPref = useVATInclPreference();

    return useMemo(() => {
        const res =
            portalCtx?.portal === 'BIZZ' &&
            portalCtx?.bizz.vatInclusive === true &&
            portalCtx?.bizz.vatRate
                ? ({
                      isInclusive: true,
                      /** The VAT rate in percentage
                       * @example 21 */
                      rate: portalCtx.bizz.vatRate,
                  } as const)
                : portal === 'WEB' && vatPref.enabled
                  ? ({
                        isInclusive: true,
                        rate: vatPref.rate,
                    } as const)
                  : ({
                        isInclusive: false,
                        rate: null,
                    } as const);

        type AllowedUnit = Money.EUR | Money.EUR_Cents | number;
        return Object.assign(res, {
            calcEUR<T extends AllowedUnit>(
                eur: T,
            ): T extends Money.VAT.Unit ? Money.VAT.Inclusive<T> : number {
                return (res.isInclusive
                    ? (eur * (100 + res.rate)) / 100
                    : eur) satisfies number as any;
            },
            removeFromEUR<T extends AllowedUnit>(
                eurWithVAT: T,
            ): T extends Money.VAT.Unit ? Money.VAT.Exclusive<T> : number {
                return (res.isInclusive
                    ? (eurWithVAT / (100 + res.rate)) * 100
                    : eurWithVAT) satisfies number as any;
            },
        });
    }, [portal, vatPref.enabled, vatPref.rate]);
}

export const VATKindDisplay: SpecificRFC<
    {
        hideParentheses?: boolean;
    },
    string
> = props => {
    const vat = useVATRate();
    const i18n = useI18n();

    const content = vat.isInclusive ? i18n._('VAT incl.') : i18n._('VAT excl.');

    return props.hideParentheses ? content : `(${content})`;
};
